import Loading from "components/common/Loading";
import CartDialog from "components/user/cart/Index";
import UserDetailPopUp from "./UserDetailPopUp";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { useEffect, useMemo, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import Login from "screens/auth/Login";
import Register from "screens/auth/Register";
import { fetchInitialData } from "store/slices/main.slice";
import MainPage from "./MainPage";
import MyOrder from "./MyOrder";
import ShopClosed from "./ShopClosed";
import { Modal, Box, Typography } from "@mui/material";
import Button from "components/common/Button";
import GlobalSearchProduct from "components/user/GlobalSearchProduct";
import { useCachedImage } from "hooks/useCachedImage";
import { resolveUploadAssetUrl } from "utils/media";

const UserContainer = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const loading = useAppSelector(({ main }) => main.loading);
  const { settings, companyData, isSelectGuestUser } = useAppSelector(
    ({ main }) => main,
  );

  const OnlineOrderingStatus = useAppSelector(
    ({ main }) => main.settings.OnlineOrderingStatus,
  );
  const [showPopup, setShowPopup] = useState(false);
  const [orderTotal, setOrderTotal] = useState(0);
  const handleClosePopup = () => setShowPopup(false);
  const resolvedLogoUrl = useMemo(
    () => resolveUploadAssetUrl(companyData?.logoUrl),
    [companyData?.logoUrl]
  );
  const logoUrl = useCachedImage(resolvedLogoUrl, { cacheKey: "company-logo" });

  useEffect(() => {
    dispatch(fetchInitialData(navigate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <Loading />;

  if (OnlineOrderingStatus) return <ShopClosed />;
  return (
    <>
      <style>{`
        @keyframes successShadeDriftA {
          0% { transform: translate3d(-8%, -6%, 0) scale(1); opacity: 0.42; }
          50% { transform: translate3d(8%, 4%, 0) scale(1.08); opacity: 0.62; }
          100% { transform: translate3d(-8%, -6%, 0) scale(1); opacity: 0.42; }
        }
        @keyframes successShadeDriftB {
          0% { transform: translate3d(10%, -4%, 0) scale(1.04); opacity: 0.28; }
          50% { transform: translate3d(-6%, 8%, 0) scale(1.14); opacity: 0.46; }
          100% { transform: translate3d(10%, -4%, 0) scale(1.04); opacity: 0.28; }
        }
      `}</style>
      <Routes>
        <Route path="/" element={<MainPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/myOrders" element={<MyOrder />} />
      </Routes>
      <CartDialog setShowPopup={setShowPopup} setOrderTotal={setOrderTotal} />
      <UserDetailPopUp />
      <GlobalSearchProduct />
      <Modal open={showPopup} onClose={handleClosePopup}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: 320, sm: 460, md: 540 },
            overflow: "hidden",
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(249,247,242,0.98) 100%)",
            borderRadius: "28px",
            border: "1px solid rgba(148, 163, 184, 0.18)",
            boxShadow: "0 28px 90px rgba(15, 23, 42, 0.28)",
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              overflow: "hidden",
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                width: { xs: 220, md: 280 },
                height: { xs: 220, md: 280 },
                top: -70,
                left: -60,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(249,115,22,0.20) 0%, rgba(249,115,22,0.06) 42%, rgba(249,115,22,0) 72%)",
                filter: "blur(8px)",
                animation: "successShadeDriftA 14s ease-in-out infinite",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                width: { xs: 220, md: 300 },
                height: { xs: 220, md: 300 },
                right: -80,
                bottom: -90,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(34,197,94,0.16) 0%, rgba(59,130,246,0.08) 45%, rgba(59,130,246,0) 74%)",
                filter: "blur(10px)",
                animation: "successShadeDriftB 17s ease-in-out infinite",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0) 38%, rgba(15,23,42,0.04) 100%)",
              }}
            />
          </Box>

          <Box
            sx={{
              position: "relative",
              px: { xs: 2.5, sm: 3.5, md: 4.5 },
              pt: { xs: 2.5, sm: 3, md: 3.5 },
              pb: { xs: 3, sm: 3.5, md: 4 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.75,
                mb: 2.5,
              }}
            >
              {!!logoUrl && (
                <Box
                  component="img"
                  src={logoUrl}
                  alt={companyData?.companyName || "Company logo"}
                  sx={{
                    width: { xs: 78, md: 92 },
                    height: { xs: 78, md: 92 },
                    objectFit: "contain",
                    borderRadius: "24px",
                    backgroundColor: "rgba(255,255,255,0.88)",
                    p: 1.25,
                    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
                    border: "1px solid rgba(255,255,255,0.7)",
                  }}
                />
              )}

              <Box
                sx={{
                  width: "100%",
                  borderRadius: "22px",
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 1.25, sm: 1.5 },
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.74) 0%, rgba(255,255,255,0.34) 100%)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255,255,255,0.5)",
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    mb: 0,
                    fontSize: { xs: "1.35rem", md: "1.6rem" },
                    color: "#111827",
                    fontWeight: 900,
                    letterSpacing: "0.02em",
                    textTransform: "uppercase",
                  }}
                >
                  {settings.ShowCompanyName && companyData?.companyName
                    ? companyData?.companyName
                    : ""}
                </Typography>
              </Box>
            </Box>

            <Typography
              variant="body1"
              gutterBottom
              sx={{
                fontSize: { xs: "1.05rem", md: "1.35rem" },
                mb: 1.25,
                color: "#111827",
                fontWeight: 500,
              }}
            >
              Your Total is: <strong>${orderTotal.toFixed(2)}</strong>
            </Typography>

            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: "1rem", md: "1.08rem" },
                color: "#1f2937",
                lineHeight: 1.7,
              }}
            >
              Your Order is placed successfully.
              <br />
              Thank you, visit again...!
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                mt: 3,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {!isSelectGuestUser && (
                <Button
                  className="!rounded-[14px] !px-6 !py-3"
                  onClick={() => (window.location.href = "/myOrders")}
                >
                  Order Status
                </Button>
              )}

              <Button
                className="!rounded-[14px] !px-6 !py-3"
                onClick={() => (window.location.href = "/")}
              >
                Go To Home Page
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default UserContainer;
